type MobileSubmenuConstructorProps = {
  expandedClass: string;
  target: HTMLElement;
  toggleElement: HTMLButtonElement;
};

export class MobileSubmenu {
  expandedClass: string;
  target: HTMLElement;
  toggleElement: HTMLButtonElement;

  constructor({
    expandedClass,
    target,
    toggleElement,
  }: MobileSubmenuConstructorProps) {
    this.expandedClass = expandedClass;
    this.target = target;
    this.toggleElement = toggleElement;
  }

  addEventListeners = () => {
    // Remove any event listeners that exist so calling this
    // method twice doesn't cause undesired behavior.
    this.removeEventListeners();

    this.toggleElement.addEventListener("click", this.toggle);
  };

  removeEventListeners = () => {
    this.toggleElement.removeEventListener("click", this.toggle);
  };

  toggle = () => {
    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  };

  isOpen = () => {
    return this.toggleElement.getAttribute("aria-expanded")?.toLowerCase() ===
      "true"
      ? true
      : false;
  };

  open = () => {
    this.target.classList.add(this.expandedClass);
    this.toggleElement.setAttribute("aria-expanded", "true");
  };

  close = () => {
    this.target.classList.remove(this.expandedClass);
    this.toggleElement.setAttribute("aria-expanded", "false");
  };
}

type MobileMenuConstructorProps = {
  expandedClass: string;
  headerElement: HTMLElement;
  menuElement: HTMLElement;
  submenus: MobileSubmenu[];
  toggleElement: HTMLButtonElement;
  toggleCloseMenuLabel: string;
  toggleOpenMenuLabel: string;
};

export class MobileMenu {
  expandedClass: string;
  headerElement: HTMLElement;
  menuElement: HTMLElement;
  submenus: MobileSubmenu[];
  toggleElement: HTMLButtonElement;
  toggleCloseMenuLabel: string;
  toggleOpenMenuLabel: string;

  constructor({
    expandedClass,
    headerElement,
    menuElement,
    submenus,
    toggleElement,
    toggleCloseMenuLabel,
    toggleOpenMenuLabel,
  }: MobileMenuConstructorProps) {
    this.expandedClass = expandedClass;
    this.headerElement = headerElement;
    this.menuElement = menuElement;
    this.submenus = submenus;
    this.toggleElement = toggleElement;
    this.toggleCloseMenuLabel = toggleCloseMenuLabel;
    this.toggleOpenMenuLabel = toggleOpenMenuLabel;
  }

  addEventListeners = () => {
    // Remove any event listeners that exist so calling this
    // method twice doesn't cause undesired behavior.
    this.removeEventListeners();

    this.toggleElement.addEventListener("click", this.toggle);
    window.removeEventListener("click", this._handleClickAway);
    window.removeEventListener("keydown", this._handleEscapeKey);
  };

  removeEventListeners = () => {
    this.toggleElement.removeEventListener("click", this.toggle);
    window.removeEventListener("keydown", this._handleEscapeKey);
  };

  toggle = () => {
    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  };

  isOpen = () => {
    return this.toggleElement.getAttribute("aria-expanded")?.toLowerCase() ===
      "true"
      ? true
      : false;
  };

  open = () => {
    this.menuElement.classList.add(this.expandedClass);
    this.toggleElement.setAttribute("aria-expanded", "true");
    this.toggleElement.setAttribute("aria-label", this.toggleCloseMenuLabel);

    window.addEventListener("click", this._handleClickAway);
    window.addEventListener("keydown", this._handleEscapeKey);
  };

  close = () => {
    this.menuElement.classList.remove(this.expandedClass);
    this.toggleElement.setAttribute("aria-expanded", "false");
    this.toggleElement.setAttribute("aria-label", this.toggleOpenMenuLabel);

    for(const submenu of this.submenus) {
      submenu.close();
    }
  };

  _handleClickAway = (event: Event) => {
    if (!event.target) return;
    if (this.headerElement.contains(event.target as Node)) return;

    this.close();
  };

  _handleEscapeKey = (event: KeyboardEvent) => {
    if (event.key !== "Escape") return;

    this.close();
  };
}
